import { useCallback, useMemo } from 'react';
import {
    keepPreviousData,
    useInfiniteQuery,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';
import {
    fetchDmNotificationPatient,
    fetchDmNotifications,
    type DmNotificationFilters,
    type DmNotificationPatientRow,
    type DmNotificationsMeta,
    type DmNotificationsParams
} from '../services/dmNotifications';
import { uniqueByPatient } from '../utils/uniqueByPatient';
import { DM_NOTIFICATIONS_KEY, dmInboxFilterKeyParts } from './queryKeys';

const STALE_TIME_MS = 60_000;
const NO_ROWS: DmNotificationPatientRow[] = [];

type DmNotificationsQueryParams = DmNotificationsParams & { enabled?: boolean };
type DmNotificationsFilterParams = Omit<DmNotificationsParams, 'page'>;

function filterKeyParts(params: DmNotificationsFilterParams): string[] {
    return [String(params.limit), ...dmInboxFilterKeyParts(params)];
}

function useRefreshDmNotifications() {
    const queryClient = useQueryClient();
    // The prefix covers both the paged and the infinite query.
    return useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: [DM_NOTIFICATIONS_KEY] });
    }, [queryClient]);
}

export function useDmNotifications({ enabled = true, ...params }: DmNotificationsQueryParams) {
    const refresh = useRefreshDmNotifications();
    const { data, isLoading, isFetching, isPlaceholderData, isError, error } = useQuery({
        queryKey: [DM_NOTIFICATIONS_KEY, 'paged', params.page, ...filterKeyParts(params)],
        queryFn: () => fetchDmNotifications(params),
        staleTime: STALE_TIME_MS,
        placeholderData: keepPreviousData,
        enabled
    });

    // Null until the first response lands, so callers can tell "loading" from "zero".
    const meta: DmNotificationsMeta | null = data?.meta ?? null;

    return {
        rows: data?.data ?? NO_ROWS,
        meta,
        isLoading,
        isFetching,
        isPlaceholderData,
        isError,
        error,
        refresh
    };
}

export function useInfiniteDmNotifications({
    enabled = true,
    ...params
}: DmNotificationsFilterParams & { enabled?: boolean }) {
    const refresh = useRefreshDmNotifications();
    const {
        data,
        isLoading,
        isFetching,
        isFetchingNextPage,
        isPlaceholderData,
        isError,
        error,
        hasNextPage,
        fetchNextPage
    } = useInfiniteQuery({
        queryKey: [DM_NOTIFICATIONS_KEY, 'infinite', ...filterKeyParts(params)],
        initialPageParam: 1,
        queryFn: ({ pageParam }) => fetchDmNotifications({ ...params, page: pageParam }),
        getNextPageParam: (lastPage) =>
            lastPage.meta.page < lastPage.meta.totalPages ? lastPage.meta.page + 1 : undefined,
        staleTime: STALE_TIME_MS,
        placeholderData: keepPreviousData,
        enabled
    });

    const rows = useMemo(
        () => (data ? uniqueByPatient(data.pages.flatMap((page) => page.data)) : NO_ROWS),
        [data]
    );
    const meta: DmNotificationsMeta | null = data?.pages[0]?.meta ?? null;

    return {
        rows,
        meta,
        isLoading,
        isFetching,
        isFetchingNextPage,
        isPlaceholderData,
        isError,
        error,
        hasNextPage,
        fetchNextPage,
        refresh
    };
}

// Under the list's prefix, so a review or assignment anywhere refreshes the open patient too.
export function useDmNotificationPatient(
    patientId: string,
    filters: DmNotificationFilters,
    { enabled = true }: { enabled?: boolean } = {}
) {
    return useQuery({
        queryKey: [DM_NOTIFICATIONS_KEY, 'patient', patientId, ...dmInboxFilterKeyParts(filters)],
        queryFn: () => fetchDmNotificationPatient(patientId, filters),
        staleTime: STALE_TIME_MS,
        enabled: enabled && patientId.length > 0
    });
}
